'use client';
import React, { useMemo, useRef, useEffect } from 'react';
import { motion, animate, motionValue } from 'framer-motion';
import { cn } from '../../lib/utils';

/** État global minimal pour synchroniser UNIQUEMENT le départ */
let __shimmerStarted = false;
let __sharedPos: ReturnType<typeof motionValue<string>> | null = null;
const getSharedPos = () => (__sharedPos ??= motionValue<string>('-50%'));

interface TextShimmerAltProps {
  children: React.ReactNode;
  as?: React.ElementType;
  className?: string;
  duration?: number;
  spread?: number;
}

export function TextShimmerAlt({
  children,
  as: Component = 'p',
  className,
  duration = 2,
  spread = 2,
}: TextShimmerAltProps) {
  const MotionComponent = motion(Component as any);
  const ref = useRef<HTMLElement | null>(null);

  const uniqueId = useMemo(
    () => `shimmer-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  const sharedPos = useMemo(getSharedPos, []);
  const dynamicSpread = useMemo(() => `${spread * 50}px`, [spread]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !__shimmerStarted) {
            __shimmerStarted = true;
            // Démarre UNE SEULE fois pour toutes les instances
            animate(sharedPos, '150%', { duration, ease: 'linear' });
            break;
          }
        }
      },
      { root: null, rootMargin: '0px 0px -100px 0px', threshold: 0 }
    );
    if (ref.current) io.observe(ref.current);

    // Si déjà lancé avant le montage de cette instance, ne rien changer
    return () => io.disconnect();
  }, [sharedPos, duration]);

  return (
    <MotionComponent
      ref={ref}
      className={cn('relative inline-block', uniqueId, className)}
      /* Pas de whileInView ni background-attachment: fixed.
         Le mouvement est piloté par la MotionValue partagée. */
      style={
        {
          ['--bg-position' as any]: sharedPos as unknown as string,
          ['--spread' as any]: dynamicSpread,
          ['--base-color' as any]: 'rgb(17, 24, 39)', // text-gray-900
          ['--base-color-secondary' as any]: 'rgb(107, 114, 128)', // text-gray-500
        } as React.CSSProperties
      }
    >
      <style>{`
        .${uniqueId} {
          position: relative;
          display: block;
          width: 100%;
          overflow: visible;
        }
        .${uniqueId},
        .${uniqueId} h1,
        .${uniqueId} h2,
        .${uniqueId} h3,
        .${uniqueId} h4,
        .${uniqueId} h5,
        .${uniqueId} h6,
        .${uniqueId} p {
          background-image: linear-gradient(
            90deg,
            var(--base-color) 0%,
            var(--base-color) calc(var(--bg-position) - var(--spread)),
            #ffffff calc(var(--bg-position) - 10px),
            #ffffff var(--bg-position),
            #ffffff calc(var(--bg-position) + 10px),
            var(--base-color) calc(var(--bg-position) + var(--spread)),
            var(--base-color) 100%
          );
          background-size: 200% 100%;
          background-position: 0 center;
          -webkit-background-clip: text !important;
          background-clip: text !important;
          color: transparent !important;
          -webkit-text-fill-color: transparent !important;
          background-color: transparent !important;
          text-shadow: none !important;
          -webkit-text-stroke: 0 !important;
          text-stroke: 0 !important;
        }

        .${uniqueId} .tw-text-gray-500,
        .${uniqueId} span[class*="tw-text-gray"] {
          background-image: linear-gradient(
            90deg,
            var(--base-color-secondary) 0%,
            var(--base-color-secondary) calc(var(--bg-position) - var(--spread)),
            #ffffff calc(var(--bg-position) - 10px),
            #ffffff var(--bg-position),
            #ffffff calc(var(--bg-position) + 10px),
            var(--base-color-secondary) calc(var(--bg-position) + var(--spread)),
            var(--base-color-secondary) 100%
          );
          background-size: 200% 100%;
          background-position: 0 center;
          -webkit-background-clip: text !important;
          background-clip: text !important;
          color: transparent !important;
          -webkit-text-fill-color: transparent !important;
        }

        .dark .${uniqueId},
        .dark .${uniqueId} h1,
        .dark .${uniqueId} h2,
        .dark .${uniqueId} h3,
        .dark .${uniqueId} h4,
        .dark .${uniqueId} h5,
        .dark .${uniqueId} h6,
        .dark .${uniqueId} p {
          --base-color: rgb(255, 255, 255) !important;
        }
        .dark .${uniqueId} .dark\\:tw-text-gray-400,
        .dark .${uniqueId} span[class*="dark\\:tw-text-gray"] {
          --base-color-secondary: rgb(156, 163, 175) !important;
          background-image: linear-gradient(
            90deg,
            rgb(156, 163, 175) 0%,
            rgb(156, 163, 175) calc(var(--bg-position) - var(--spread)),
            #ffffff calc(var(--bg-position) - 10px),
            #ffffff var(--bg-position),
            #ffffff calc(var(--bg-position) + 10px),
            rgb(156, 163, 175) calc(var(--bg-position) + var(--spread)),
            rgb(156, 163, 175) 100%
          );
        }

        .${uniqueId} * {
          background-size: 200% 100%;
          background-position: 0 center;
        }

        .${uniqueId},
        .${uniqueId} * {
          will-change: background-position;
          -webkit-font-smoothing: antialiased;
        }
      `}</style>

      {children}
    </MotionComponent>
  );
}